import { supabase } from "./supabase"
import type { User } from "@supabase/supabase-js"

export async function signUp(email: string, password: string, username: string, displayName: string): Promise<User> {
  // Check if username is available
  const isAvailable = await checkUsernameAvailable(username)
  if (!isAvailable) {
    throw new Error("Username is already taken")
  }

  // Check if email is already registered in our users table
  const { data: existingUser, error: emailCheckError } = await supabase.from("users").select("id").eq("email", email.toLowerCase()).maybeSingle()

  console.log("Email check:", { email: email.toLowerCase(), existingUser, emailCheckError })

  if (existingUser) {
    throw new Error("An account with this email already exists. Try signing in instead.")
  }

  // Sign up with Supabase Auth
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        username,
        display_name: displayName,
      },
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  })

  if (error) throw error
  if (!data.user) throw new Error("Failed to create user")

  // Supabase returns a user with empty identities if the email is already registered
  if (data.user.identities && data.user.identities.length === 0) {
    throw new Error("An account with this email already exists. Try signing in instead.")
  }

  return data.user
}

export async function signIn(email: string, password: string): Promise<User> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })

  if (error) throw error
  if (!data.user) throw new Error("Failed to sign in")

  return data.user
}

export async function signInWithGoogle(): Promise<void> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
      queryParams: {
        access_type: "offline",
        prompt: "consent",
      },
    },
  })

  if (error) throw error
  // OAuth will redirect to Google, then back to /auth/callback
}

export async function signInWithGithub(): Promise<void> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "github",
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  })

  if (error) throw error
  // OAuth will redirect to GitHub, then back to /auth/callback
}

export async function logOut(): Promise<void> {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export async function resetPassword(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/reset-password`,
  })
  if (error) throw error
}

export async function checkUsernameAvailable(username: string): Promise<boolean> {
  const { data, error } = await supabase.from("users").select("username").eq("username", username.toLowerCase()).single()

  if (error && error.code !== "PGRST116") {
    // PGRST116 is "not found" error, which means username is available
    throw error
  }

  return !data
}

export async function getCurrentUser(): Promise<User | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  return user
}
